import { FC, useEffect, useRef, useState } from "react";
import "./ChatInput.scss";
import { BvIcon } from "bevi-icon";
import { useChats } from "../../../providers/Chats/ChatsProvider";
import ChatQuickActions, {
  type ChildrenType,
} from "../ChatQuickActions/ChatQuickActions";
import { useChatsTutorial } from "../../../providers/Chats/ChatsTutorialProvider";
import type { FileAttachment } from "../../../models/Chat/ChatMessageModel";

interface ChatInputProps {
  isTutorial?: boolean;
}

const ChatInput: FC<ChatInputProps> = ({ isTutorial = false }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string>("");
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [quickActionsRender, setQuickActionsRender] = useState<boolean>(false);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { message, setMessage, isStreaming, handleSend, handleStop, state, activeAssistantId } =
    useChats();
  const {
    setMessageTutorial,
    messageTutorial,
    handleSendTutorial,
    stepTutorial,
  } = useChatsTutorial();

  const MAX_FILE_SIZE = 25 * 1024 * 1024;
  const MAX_TEXTAREA_HEIGHT = 180;

  const autoResize = () => {
    const ta = textareaRef.current;
    if (!ta) return;

    ta.style.height = "0px";
    const scrollHeight = ta.scrollHeight;
    const newHeight = Math.min(scrollHeight, MAX_TEXTAREA_HEIGHT);

    ta.style.height = `${newHeight}px`;
    ta.style.overflowY = scrollHeight > MAX_TEXTAREA_HEIGHT ? "auto" : "hidden";
  };

  useEffect(() => {
    autoResize();
  }, [message, messageTutorial]);

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadError("");
    setFilePreview(null);

    if (files.length === 0) return;

    if (files.length > 1) {
      setUploadError("Por favor, selecione apenas um arquivo por mensagem");
      return;
    }

    const file = files[0];

    const allowedExtensions = ["jpg", "jpeg", "png", "pdf", "webp"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";

    if (!allowedExtensions.includes(fileExtension)) {
      setUploadError("Tipo de arquivo não permitido. Extensões aceitas: JPG, JPEG, PNG, PDF, WebP");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadError("Arquivo muito grande. Máximo 25MB permitido");
      return;
    }

    setSelectedFiles([file]);

    const isImage = file.type.startsWith("image/");
    if (isImage) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFilePreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = () => {
    setSelectedFiles([]);
    setFilePreview(null);
  };

  const toggleQuickAction = () => {
    setQuickActionsRender(!quickActionsRender);
  };

  const quickActionSelected = (child: ChildrenType) => {
    handleSend(child.payload);
  };

  const getFileIcon = (file: File) => {
    if (file.type === "application/pdf") {
      return { name: "pdf", variant: "dark" };
    }
    return { name: "description" };
  };

  const tutorialAnswers: Record<number, string> = {
    0: "OLÁ",
    1: "VAMOS",
    2: "QUERO",
    3: "OK",
    4: "SIM",
    5: "OURO",
    6: "PERFEITO",
    7: "VAMOOOS",
  };

  const validateTutorialMessage = (message: string) => {
    const correct = tutorialAnswers[stepTutorial];

    if (message.toUpperCase() === correct) {
      setErrorMessage("");
      return true;
    }

    setErrorMessage(correct);
    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();

      if (isTutorial) {
        if (messageTutorial.trim()) sendMessage();
      } else {
        if (message.trim() || selectedFiles.length > 0) sendMessage();
      }
    }
  };

  const sendMessage = () => {
    if (isTutorial) {
      const trimmed = messageTutorial.trim();
      if (!trimmed) return;

      const isValid = validateTutorialMessage(trimmed);
      if (!isValid) return;

      handleSendTutorial(trimmed);
    } else {
      const trimmed = message.trim();
      if (!trimmed && selectedFiles.length === 0) return;
      handleSend(trimmed, undefined, selectedFiles.length > 0 ? selectedFiles : undefined);
    }

    setMessage("");
    setMessageTutorial("");
    setSelectedFiles([]);

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "0px";
        textareaRef.current.style.height = `24px`;
        textareaRef.current.style.overflowY = "hidden";
        textareaRef.current.focus();
      }
    }, 50);
  };

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  return (
    <section
      className="chat-input"
      data-testid="ChatInput"
      data-state={state}
    >
      {stepTutorial > 0 && isTutorial && errorMessage && (
        <div className="container-chat flex-start flex-column gap-2 text-danger">
          <span className="me-1">Digite exatamente:</span>
          <b>{errorMessage}</b>
        </div>
      )}
      <div className="container-chat flex-center flex-column gap-2">
        <div className="chat-input__title-content w-100" data-state={state}>
          <div
            className={`d-flex flow-column ${state === "tutorial" ? "justify-content-start" : "justify-content-center"} align-items-start gap-2`}
          >
            <img
              src="/assets/img/bevi-ajuda-icon.svg"
              width={42}
              height={42}
              alt="Logo do Bevi ajuda com um simbulo de chat e a silha IA"
            />
            <h1>Bem-vindo à IA Beviajuda</h1>
          </div>
          {state === "tutorial" && (
            <>
              <p>Digite "Olá" para iniciar o tutorial.</p>
            </>
          )}
          {state === "tutorial" && errorMessage && (
            <div className="flex-start flex-column gap-2 text-danger">
              <span className="me-1">Digite exatamente:</span>
              <b>{errorMessage}</b>
            </div>
          )}
        </div>
        <div
          className="chat-input__content w-100 flex flex-column justify-content-start gap-2"
          onClick={(e) => {
            const textarea = e.currentTarget.querySelector("textarea");
            textarea?.focus();
          }}
          data-state={state}
        >
          {selectedFiles.length > 0 && (
            <div className="chat-input__file-preview-container w-100">
              {selectedFiles.map((file, idx) => {
                const fileIcon = getFileIcon(file);
                const isImage = file.type.startsWith("image/");
                return (
                  <div key={idx} style={{ position: "relative" }}>
                    {isImage ? (
                      <>
                        {filePreview && (
                          <div
                            className="chat-input__file-card-image"
                            style={{ backgroundImage: `url(${filePreview})` }}
                          />
                        )}
                        <button
                          className="all-unset chat-input__file-card-remove"
                          onClick={removeFile}
                          title="Remover arquivo"
                          style={{
                            position: "absolute",
                            top: "-8px",
                            right: "-8px",
                            backgroundColor: "white",
                            borderRadius: "50%",
                            padding: "2px",
                          }}
                        >
                          <BvIcon name="add" size={0.8} weight={600} />
                        </button>
                      </>
                    ) : (
                      <div className="chat-input__file-card">
                        <div className="chat-input__file-card-top">
                          <span className="chat-input__file-card-name">{file.name}</span>
                          <button
                            className="all-unset chat-input__file-card-remove"
                            onClick={removeFile}
                            title="Remover arquivo"
                          >
                            <BvIcon name="add" size={1.2} weight={600} />
                          </button>
                        </div>
                        <div className="chat-input__file-card-bottom">
                          {fileIcon.variant ? (
                            <BvIcon name={fileIcon.name} variant={fileIcon.variant as any} size={1.6} />
                          ) : (
                            <BvIcon name={fileIcon.name} size={1.6} />
                          )}
                          <span className="chat-input__file-card-type">
                            {file.type.split("/").pop()?.toUpperCase() || "FILE"}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="chat-input__input-row w-100">
            {!isTutorial ? (
              <div className="position-relative">
                <button
                  ref={buttonRef}
                  title="Ações rápidas"
                  className="all-unset chat-input__btn chat-input__btn-actions flex-center"
                  data-state={quickActionsRender ? "show" : "hidden"}
                  onClick={toggleQuickAction}
                >
                  <BvIcon name="add" size={1.5} weight={600} />
                </button>
                <ChatQuickActions
                  isOpen={quickActionsRender}
                  onClose={() => setQuickActionsRender(false)}
                  buttonRef={buttonRef}
                  onElementSelected={quickActionSelected}
                  onAttachClick={handleAttachClick}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                  accept="image/*,.pdf"
                />
              </div>
            ) : null}

            <textarea
              ref={textareaRef}
              value={isTutorial ? messageTutorial : message}
              onChange={(e) => {
                isTutorial
                  ? setMessageTutorial(e.target.value.toLocaleUpperCase())
                  : setMessage(e.target.value);
              }}
              placeholder="Digite sua mensagem..."
              className="chat-input__textarea"
              readOnly={isStreaming}
              onKeyDown={handleKeyDown}
            />

            {!isTutorial && isStreaming ? (
              <button
                type="button"
                className="all-unset chat-input__btn chat-input__btn-send flex-center"
                title="Parar geração"
                onClick={handleStop}
              >
                <span
                  aria-label="Parar"
                  style={{
                    display: "block",
                    width: "24px",
                    height: "24px",
                    borderRadius: "3px",
                    backgroundColor: "#fff",
                  }}
                />
              </button>
            ) : (
              <button
                type="button"
                className="all-unset chat-input__btn chat-input__btn-send flex-center"
                disabled={isTutorial ? false : (!message.trim() && selectedFiles.length === 0) || !activeAssistantId}
                onClick={sendMessage}
              >
                <BvIcon
                  name="arrowForward"
                  size={1.5}
                  weight={600}
                  className={`${!message || !activeAssistantId ? "is-disabled" : ""}`}
                />
              </button>
            )}
          </div>
        </div>

        {uploadError && (
          <div className="chat-input__error w-100">
            <span className="text-danger">{uploadError}</span>
          </div>
        )}

        {!isTutorial && (
          <span className="info" data-state={state}>
            Beviajuda pode cometer erros. Verifique as informações.
          </span>
        )}
      </div>
    </section>
  );
};

export default ChatInput;
